import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Row, Column, List, Modal } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image, CTA, Video } from '../../components';
import { setBackgroundColor, parseQuery } from '../../components/utilities/functions';
import { isValidArray } from '../../components/utilities/validations';
import ImageBanner from '../../components/common/image-banner';
import DynamicComponent from '../../components/common/dynamic-component';
import PerformancePanel from '../../components/common/performance-panel';
import Feature from '../../components/common/feature';
import AnchorLinks from '../../components/common/anchor-links';
import CTARibbon from '../../components/common/cta-ribbon';
import PCCForm from '../../components/common/forms/find-a-contractor/pcc-form';
import RTAFreeConsultation from '../../components/common/forms/rta-free-consultation';
import BranchOffer from '../../components/common/branch-offer';
import DistributorApplication from '../../components/common/forms/distributor-application';
import EbookForm from '../../components/common/forms/ebook-form';
import CardSection from '../../components/common/card-section';
import RequestProjectQuote from '../../components/common/forms/request-project-quote';
import HeroSimple from '../../components/common/hero-simple';
import styles from './index.module.scss';

const Marketing = (props) => {
	const [pageData, setPageData] = useState(props.data.contentstackMarketing);
	const [modalOpen, setModalOpen] = useState(false);
	const [lang, setLang] = useState('en');
	const utm = props.location.search ? parseQuery(props.location.search) : null;

	// Opening form based on button id
	useEffect(() => {
		setTimeout(() => {
			const btn = document.getElementById('js-ebook-trigger');

			if (btn) {
				btn.addEventListener('click', () => onEbookTrigger());
				return () => btn.removeEventListener('click', () => onEbookTrigger());
			}
		}, 0);
	}, []);

	useEffect(() => {
		if (lang === 'es' && pageData.english_spanish && props.data.spanish) {
			setPageData(props.data.spanish);
		} else {
			setPageData(props.data.contentstackMarketing);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [lang]);

	const rowAlign = (value) => {
		switch (value) {
			case 'top':
				return 'start';
			case 'bottom':
				return 'end';
			case 'center':
				return 'center';
			case 'stretch':
				return 'stretch';
			default:
				return null;
		}
	};

	const rowJustify = (value) => {
		switch (value) {
			case 'left':
				return 'start';
			case 'right':
				return 'end';
			case 'center':
				return 'center';
			case 'between':
				return 'between';
			case 'around':
				return 'around';
			default:
				return null;
		}
	};

	const setTextAlign = (value) => {
		switch (value) {
			case 'right':
				return styles.textRight;
			case 'center':
				return styles.textCenter;
			default:
				return '';
		}
	};

	const renderForm = (type) => {
		switch (type) {
			case 'find a pcc':
				return (
					<Container width={1200}>
						<PCCForm />
					</Container>
				);
			case 'pro quote':
				return (
					<Container width={1200}>
						<RequestProjectQuote
							path={props.location.origin + props.location.pathname}
							utm={utm}
						/>
					</Container>
				);
			case 'rta form':
				return (
					<Container width={1200}>
						<RTAFreeConsultation
							buttonText='Get Started'
							className={styles.marketSpecial_RTA}
						/>
					</Container>
				);
			case 'national offer':
				return <BranchOffer showDisclaimers={true} />;
			case 'distributor':
				return (
					<Container width={1200}>
						<DistributorApplication />
					</Container>
				);
			case 'ebook':
				return (
					<Modal
						isOpen={modalOpen}
						onClose={onEbookClose}
						width={500}
						align='center'
						className={styles.generalEbookModal}
					>
						<Modal.Content>
							<EbookForm
								path={props.location.origin + props.location.pathname}
								utm={utm}
							/>
						</Modal.Content>
					</Modal>
				);
			case 'rta-no-container':
				return (
					<RTAFreeConsultation
						className={styles.marketExpected_RTA}
						title={pageData.rta?.heading}
						content={pageData.rta?.description}
					/>
				);
			default:
				return null;
		}
	};

	const onEbookTrigger = () => setModalOpen(true);
	const onEbookClose = () => setModalOpen(false);
	const onTranslateClick = (language) => setLang(language);

	return (
		<Layout
			id='template-Marketing'
			className={pageData.black_background ? styles.bgBlack : null}
			hideWarranty={pageData.remove_warranty}
			hideLeadGen={pageData.remove_lead_gen}
		>
			{pageData.breadcrumb && (
				<Breadcrumbs
					data={pageData.breadcrumb}
					currentUrl={pageData.url}
					className={styles.marketBreadcrumbs}
				/>
			)}

			{pageData.english_spanish && (
				<div className={styles.marketTranslate}>
					<Container>
						<List
							bullets='none'
							layout='fluid'
						>
							<li>
								<button
									onClick={() => onTranslateClick('en')}
									className={lang === 'en' ? styles.marketTranslate_active : ''}
								>
									English
								</button>
							</li>
							<li>
								<button
									onClick={() => onTranslateClick('es')}
									className={lang === 'es' ? styles.marketTranslate_active : ''}
								>
									Spanish
								</button>
							</li>
						</List>
					</Container>
				</div>
			)}

			{isValidArray(pageData.modular_blocks) &&
				pageData.modular_blocks.map((block, index) => {
					if (isValidArray(block.hero?.hero)) {
						return (
							<HeroSimple
								data={block.hero.hero[0]}
								key={index}
							/>
						);
					}

					if (block.general_content != null) {
						return (
							<div
								className={`${styles.marketGeneral} ${styles.marketPadded} ${setTextAlign(block.general_content.text_align)} ${block.general_content.background_color ? styles[setBackgroundColor(block.general_content.background_color)] : ''}`}
								key={index}
							>
								<Container>
									{block.general_content.heading && index === 0 && <h1 dangerouslySetInnerHTML={{ __html: block.general_content.heading }} />}
									{block.general_content.heading && index !== 0 && (
										<h2
											className={styles.marketHeading}
											dangerouslySetInnerHTML={{ __html: block.general_content.heading }}
										/>
									)}
									{block.general_content.subheading && (
										<h3
											className={styles.marketGeneral_Subheading}
											dangerouslySetInnerHTML={{ __html: block.general_content.subheading }}
										/>
									)}
									{block.general_content.description && (
										<div
											className={styles.marketGeneral_Description}
											dangerouslySetInnerHTML={{ __html: block.general_content.description }}
										/>
									)}
									{isValidArray(block.general_content.icon) && (
										<List
											bullets='none'
											className={styles.marketGeneral_IconList}
										>
											{block.general_content.icon.map((img, index) => (
												<li key={index}>
													<Image
														data={{
															image: img,
															image_alt: '',
														}}
													/>
												</li>
											))}
										</List>
									)}
									{isValidArray(block.general_content.ctas) && (
										<List
											bullets='none'
											layout='inline'
											className={styles.marketGeneral_LinkList}
										>
											{block.general_content.ctas.map((link, index) => (
												<li key={index}>
													<CTA data={link} />
												</li>
											))}
										</List>
									)}
								</Container>
							</div>
						);
					}

					if (block.full_width != null) {
						return (
							<div
								key={index}
								id={block.full_width.anchor_text || null}
								className={`${styles.marketPadded} ${block.full_width.background_color ? styles[setBackgroundColor(block.full_width.background_color)] : ''}`}
							>
								<Container>
									{block.full_width.heading && <h2 className={styles.marketFW_Heading}>{block.full_width.heading}</h2>}
									{isValidArray(block.full_width.video) && <Video data={block.full_width.video[0]} />}
								</Container>
								{isValidArray(block.full_width.callout) && <ImageBanner data={block.full_width.callout[0]} />}
							</div>
						);
					}

					if (isValidArray(block.columns?.columns)) {
						return (
							<div
								id={block.columns.anchor_text || null}
								className={`${styles.marketColumns} ${block.columns.background_color ? styles[setBackgroundColor(block.columns.background_color)] : ''} ${styles.marketMargin}`}
								key={index}
							>
								<Container
									fluid={!block.columns.container}
									noPadding={!block.columns.container}
								>
									{block.columns.heading &&
										(index === 0 ? (
											<h1 dangerouslySetInnerHTML={{ __html: block.columns.heading }} />
										) : (
											<h2
												className={styles.marketHeading}
												dangerouslySetInnerHTML={{ __html: block.columns.heading }}
											/>
										))}
									{block.columns.subheading && <h3 dangerouslySetInnerHTML={{ __html: block.columns.subheading }} />}

									<Row
										align={rowAlign(block.columns.align)}
										justify={rowJustify(block.columns.justify)}
										noGutters={!block.columns.container}
									>
										{block.columns.columns.map((column, index) => (
											<Column
												key={index}
												xs={column.column_mobile || 12}
												lg={column.column_desktop || null}
											>
												<div className={`${styles.marketColumn} ${column.background_color ? styles[setBackgroundColor(column.background_color)] : ''} ${!block.columns.container && column.image_overlay_text ? styles.marketColumn_overlayText : ''} ${setTextAlign(column.text_align)}`}>
													{column.image?.[0]?.image?.url && (
														<Image
															data={column.image[0]}
															className={`${styles.marketColumn_Image} ${!block.columns.container ? styles.marketColumn_Image_wide : ''}`}
														/>
													)}
													{column.image?.[0]?.video?.video_id && (
														<Video
															data={column.image[0]}
															className={styles.marketColumn_Video}
														/>
													)}
													{column.image_overlay_text && (
														<div
															className={styles.marketColumn_OverlayText}
															dangerouslySetInnerHTML={{
																__html: column.image_overlay_text,
															}}
														/>
													)}
													{column.heading && (
														<h3
															className={styles.marketColumn_Heading}
															dangerouslySetInnerHTML={{ __html: column.heading }}
														/>
													)}
													{column.description && (
														<div
															className={styles.marketColumn_Description}
															dangerouslySetInnerHTML={{
																__html: column.description,
															}}
														/>
													)}
													{isValidArray(column.ctas) && <CTA data={column.ctas[0]} />}
													{column.add_rta && renderForm('rta-no-container')}
												</div>
											</Column>
										))}
									</Row>
								</Container>
							</div>
						);
					}

					if (isValidArray(block.cards?.dynamic)) {
						return (
							<DynamicComponent
								data={block.cards}
								key={index}
							/>
						);
					}

					if (isValidArray(block.feature?.feature)) {
						return (
							<Feature
								data={block.feature}
								key={index}
							/>
						);
					}

					if (isValidArray(block.links?.anchor_links) || isValidArray(block.links?.cta_ribbon)) {
						return (
							<div
								key={index}
								className={`${styles.marketAnchors} ${styles.marketMargin}`}
							>
								<Container>
									{block.links.subheading && <h2 className={styles.marketHeading}>{block.links.subheading}</h2>}
									{isValidArray(block.links.ctas) && <AnchorLinks data={block.links.ctas} />}
									{isValidArray(block.links.cta_ribbon) && <CTARibbon data={block.links.cta_ribbon[0]} />}
								</Container>
							</div>
						);
					}

					if (block.special != null) {
						return (
							<div
								key={index}
								id={block.special.anchor_text || null}
								className={styles.marketMargin}
							>
								{block.special.heading && (
									<Container>
										<h2 className={styles.marketHeading}>{block.special.heading}</h2>
									</Container>
								)}
								{block.special.form_type && renderForm(block.special.form_type)}
							</div>
						);
					}

					if (block.quote != null) {
						return (
							<div
								key={index}
								className={styles.marketQuote}
							>
								<Container>
									{block.quote.heading && <h2 style={styles.marketHeading}>{block.quote.heading}</h2>}
									{block.quote.quote && (
										<blockquote>
											<div dangerouslySetInnerHTML={{ __html: block.quote.quote }} />
											{block.quote.citation && <cite dangerouslySetInnerHTML={{ __html: block.quote.citation }} />}
										</blockquote>
									)}
								</Container>
							</div>
						);
					}

					if (block.new_cards != null) {
						return (
							<CardSection
								data={block.new_cards}
								key={index}
							/>
						);
					}

					if (block.performance != null) {
						return (
							<PerformancePanel
								data={block.performance}
								key={index}
							/>
						);
					}
				})}
		</Layout>
	);
};

Marketing.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default Marketing;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => <SEO {...data.contentstackMarketing.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackMarketing(id: { eq: $uid }) {
			url
			breadcrumb {
				active_page
				back_pages {
					...CTA
				}
			}
			english_spanish
			black_background
			modular_blocks {
				hero {
					hero {
						...NewHero
					}
				}
				general_content {
					text_align
					background_color
					heading
					subheading
					icon {
						url
						gatsbyImageData(quality: 90)
					}
					description
					ctas {
						...CTA
					}
				}
				full_width {
					anchor_text
					background_color
					heading
					video {
						...Video
					}
					callout {
						...ImageCallout
					}
				}
				performance {
					heading
					description
					cob {
						url
						gatsbyImageData(quality: 90)
					}
					sections {
						file {
							url
							gatsbyImageData(quality: 90)
						}
						heading
						description
					}
				}
				columns {
					anchor_text
					background_color
					container
					heading
					align
					justify
					columns {
						column_desktop
						column_mobile
						text_align
						background_color
						image {
							... on Contentstack_image {
								...Image
								image {
									gatsbyImageData(quality: 90)
								}
							}
							... on Contentstack_video {
								...Video
							}
						}
						image_overlay_text
						heading
						description
						add_rta
						ctas {
							...CTA
						}
					}
				}
				cards {
					background_color
					heading
					dynamic {
						...Dynamic
					}
				}
				feature {
					anchor_text
					background_color
					subheading
					feature {
						...Feature
					}
				}
				links {
					subheading
					ctas {
						...CTA
					}
					cta_ribbon {
						...CTARibbon
					}
				}
				special {
					anchor_text
					heading
					form_type
				}
				quote {
					heading
					quote
					citation
				}
				new_cards {
					...CardSection
				}
			}
			remove_lead_gen
			remove_warranty
			remove_yellow_bar
			rta {
				heading
				description
			}
			seo {
				title
				description
				noIndex: no_index_page
			}
		}

		spanish: contentstackMarketing(locale: { eq: "es" }) {
			url
			breadcrumb {
				active_page
				back_pages {
					...CTA
				}
			}
			english_spanish
			black_background
			modular_blocks {
				columns {
					anchor_text
					background_color
					container
					heading
					align
					justify
					columns {
						column_desktop
						column_mobile
						text_align
						background_color
						image {
							... on Contentstack_image {
								...Image
								image {
									gatsbyImageData(quality: 75)
								}
							}
							... on Contentstack_video {
								...Video
							}
						}
						image_overlay_text
						heading
						description
						add_rta
						ctas {
							...CTA
						}
					}
				}
			}
			remove_lead_gen
			remove_warranty
			remove_yellow_bar
			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
